<template>
  <v-container>
    <v-card>
      <v-card-title class="d-flex align-center justify-space-between">
        <div class="d-flex align-center">
          <v-icon class="me-2">mdi-format-list-bulleted</v-icon>
          My Lists
        </div>
        <v-btn
          color="primary"
          prepend-icon="mdi-plus"
          @click="showCreateDialog = true"
        >
          Create List
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-data-table
          :headers="headers"
          :items="lists"
          :loading="loading"
          item-value="id"
          class="elevation-1"
          @click:row="openList"
        >
          <template v-slot:item.groups="{ item }">
            {{ item.group_count || 0 }}
          </template>
          <template v-slot:item.items="{ item }">
            {{ item.item_count || 0 }}
          </template>
          <template v-slot:item.images="{ item }">
            {{ item.header_image_url ? 1 : 0 }}
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn
              icon="mdi-pencil"
              size="small"
              variant="text"
              @click.stop="editList(item)"
            ></v-btn>
            <v-btn
              icon="mdi-delete"
              size="small"
              variant="text"
              color="error"
              @click.stop="deleteList(item)"
            ></v-btn>
          </template>
        </v-data-table>
        <div v-if="!loading && lists.length === 0" class="text-center py-8">
          <v-icon size="64" color="grey">mdi-package-variant</v-icon>
          <p class="text-h6 mt-4">No lists found</p>
        </div>
      </v-card-text>
    </v-card>

    <!-- Create/Edit List Dialog -->
    <ListDialog
      v-model="showCreateDialog"
      :initial-data="null"
      @created="handleListCreated"
      @message="handleMessage"
    />
    
    <ListDialog
      v-model="showEditDialog"
      :initial-data="editingPacklist"
      @updated="handleListUpdated"
      @message="handleMessage"
    />

    <!-- Delete List Dialog -->
    <DeleteListDialog
      v-model="showDeleteDialog"
      :list-data="deletingList"
      @deleted="handleListDeleted"
      @message="handleMessage"
    />
  </v-container>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useListStore } from '../stores/list'
import { useAuthStore } from '../stores/auth'
import { useRouter } from 'vue-router'
import ListDialog from '../components/ListDialog.vue'
import DeleteListDialog from '../components/DeleteListDialog.vue'

export default {
  name: 'ListView',
  components: {
    ListDialog,
    DeleteListDialog
  },
  setup() {
    const listStore = useListStore()
    const authStore = useAuthStore()
    const router = useRouter()
    const loading = ref(false)
    
    // Use computed property to be reactive to store changes
    const lists = computed(() => listStore.lists)
    
    // Dialog states
    const showCreateDialog = ref(false)
    const showEditDialog = ref(false)
    const showDeleteDialog = ref(false)
    const editingPacklist = ref(null)
    const deletingList = ref(null)

    const headers = [
      { title: 'Name', key: 'name', align: 'start' },
      { title: 'Groups', key: 'groups', align: 'center' },
      { title: 'Items', key: 'items', align: 'center' },
      { title: 'Images', key: 'images', align: 'center' },
      { title: 'Actions', key: 'actions', align: 'center', sortable: false }
    ]

    const openList = (event, { item }) => {
      console.log('Opening list:', item)
      router.push(`/lists/${item.id}`)
    }

    const editList = (list) => {
      editingPacklist.value = list
      showEditDialog.value = true
    }

    const deleteList = (list) => {
      deletingList.value = list
      showDeleteDialog.value = true
    }

    const handleListCreated = async () => {
      // Refresh the lists after creation
      await listStore.fetchLists()
    }

    const handleListUpdated = async () => {
      // Refresh the lists after update
      await listStore.fetchLists()
    }

    const handleListDeleted = async () => {
      // Refresh the lists after deletion
      await listStore.fetchLists()
    }

    const handleMessage = (messageData) => {
      // You can implement a snackbar or toast notification here
      console.log(messageData.message)
    }

    onMounted(async () => {
      loading.value = true
      if (authStore.isAuthenticated) {
        await listStore.fetchLists()
      }
      loading.value = false
    })

    return {
      lists,
      headers,
      loading,
      openList,
      editList,
      deleteList,
      showCreateDialog,
      showEditDialog,
      showDeleteDialog,
      editingPacklist,
      deletingList,
      handleListCreated,
      handleListUpdated,
      handleListDeleted,
      handleMessage
    }
  }
}
</script>

<style scoped>
.v-data-table {
  cursor: pointer;
}
</style>
